import HiringFunnelChart from '../features/hiringFunnel/HiringFunnelChart';
import { EmployeeSkillRadarChart } from '../features/employeeSkillRadar';
import AttendanceAreaChart from '../features/attendanceArea/AttendanceAreaChart';
import LeaveHeatmapCalendar from '../features/leaveHeatmap/LeaveHeatmapCalendar';
import SalaryExperienceScatter from '../features/salaryExperienceScatter/SalaryExperienceScatter';
import { SalaryStackedBarChart } from '../features/salaryStackedBar';
import { SalaryDoughnutChart } from '../features/salaryDoughnut';
import { LeaveTypePieChart } from '../features/leaveTypePie';

import { DepartmentHeadcountChart, DepartmentHeadcountSimpleChart } from '../features/departmentHeadcount';
import { PayrollLineChart } from '../features/payrollLine';
import MainLayout from '../layout/MainLayout';


// --- Premium Metric Card ---
import { ArrowUpRight, ArrowDownRight, Users, Briefcase, DollarSign, CalendarDays } from 'lucide-react';

import ChartGlassPanel from '../components/ChartGlassPanel';


function MetricCard({ title, value, icon, trendValue, isPositive, subtitle }) {
  const Icon = icon;
  return (
    <div className="border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 bg-white">
      <div className="p-5 h-full flex flex-col justify-between">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <h3 className="text-2xl font-bold text-indigo-600 mt-1">{value}</h3>
          </div>
          <div className="bg-gray-100 p-3 rounded-lg">
            <Icon className="h-6 w-6 text-indigo-500" />
          </div>
        </div>
        <p className="flex items-center mt-3 text-sm">
          <span className={`flex items-center ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
            {isPositive ? (
              <ArrowUpRight className="h-4 w-4 animate-bounce mr-1" />
            ) : (
              <ArrowDownRight className="h-4 w-4 mr-1" />
            )}
            {trendValue}
          </span>
          <span className="ml-1 text-gray-500">{subtitle}</span>
        </p>
      </div>
    </div>
  );
}




export default function Dashboard() {
  // Example metric data (replace with real data)
  const metrics = [
    {
      title: 'Total Employees',
      value: '128',
      icon: Users,
      trend: 'up',
      trendValue: '4%',
      isPositive: true,
      subtitle: 'from last month',
    },
    {
      title: 'Open Positions',
      value: '12',
      icon: Briefcase,
      trend: 'down',
      trendValue: '2%',
      isPositive: false,
      subtitle: 'from last month',
    },
    {
      title: 'Payroll (USD)',
      value: '$120,000',
      icon: DollarSign,
      trend: 'up',
      trendValue: '8%',
      isPositive: true,
      subtitle: 'from last month',
    },
    {
      title: 'Leaves Taken',
      value: '34',
      icon: CalendarDays,
      trend: 'up',
      trendValue: '5%',
      isPositive: true,
      subtitle: 'from last month',
    },
  ];

  // Chart config array for easy future updates
  const chartConfigs = [
    {
      title: 'Salary Breakdown',
      Component: SalaryDoughnutChart,
    },
    {
      title: 'Department Headcount',
      Component: DepartmentHeadcountChart,
    },
    {
      title: 'Leave Types',
      Component: LeaveTypePieChart,
    },
    {
      title: 'Salary by Department',
      Component: SalaryStackedBarChart,
    },
    {
      title: 'Payroll Over Time',
      Component: PayrollLineChart,
    },
    {
      title: 'Employee Skills',
      Component: EmployeeSkillRadarChart,
    },
    {
      title: 'Attendance Trends',
      Component: AttendanceAreaChart,
    },
    {
      title: 'Leave Heatmap',
      Component: LeaveHeatmapCalendar,
    },
    {
      title: 'Salary vs Experience',
      Component: SalaryExperienceScatter,
    },
    {
      title: 'Hiring Funnel',
      Component: HiringFunnelChart,
    },
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-8 font-sans tracking-tight">HRMS Dashboard</h1>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {metrics.map((m, i) => (
              <MetricCard key={i} {...m} />
            ))}
          </div>
          {/* Chart Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
            {chartConfigs.map(({ title, Component }, idx) => (
              <ChartGlassPanel key={title} title={title}>
                <div className="h-full w-full">
                  <Component minimalist />
                </div>
              </ChartGlassPanel>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
